import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../hooks/useToast';
import { apiClient } from '../../../../lib/api';
import { adaptCampaignToUI, UICampaignRecipient } from '../../../../utils/campaignAdapter';
import {
  CampaignData,
  ChatMessage,
  SortField,
  SortDirection,
  PendingChanges,
} from '../types';
import { getEngagementInsight, generateChatResponse } from '../utils/campaignHelpers';
import { EmailTemplate } from './useCampaignState';

interface UseCampaignHandlersProps {
  templateId: string | undefined;
  campaign: CampaignData | null;
  editName: string;
  editSubject: string;
  editBody: string;
  editSendWindowStart: string;
  editSendWindowEnd: string;
  editSendDays: string[];
  chatInput: string;
  sortField: SortField | null;
  sortDirection: SortDirection;
  recipients: UICampaignRecipient[];
  selectedRecipientIds: Set<string>;
  setCampaign: (campaign: CampaignData | null) => void;
  setEditName: (name: string) => void;
  setIsEditingName: (editing: boolean) => void;
  setIsEditingEmail: (editing: boolean) => void;
  setIsSettingsExpanded: (expanded: boolean) => void;
  setShowChangeConfirmation: (show: boolean) => void;
  setPendingChanges: (changes: PendingChanges | null) => void;
  setChatInput: (input: string) => void;
  setChatMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setIsChatLoading: (loading: boolean) => void;
  setSortField: (field: SortField | null) => void;
  setSortDirection: (direction: SortDirection) => void;
  setExpandedRecipients: React.Dispatch<React.SetStateAction<Set<string>>>;
  setShowSendConfirmation: (show: boolean) => void;
  setIsActivating: (activating: boolean) => void;
  setRecipients: React.Dispatch<React.SetStateAction<UICampaignRecipient[]>>;
  setRecipientsTotal: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPage: (page: number) => void;
  setSelectedRecipientIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setEmailTemplates: React.Dispatch<React.SetStateAction<EmailTemplate[]>>;
  incrementRecipientsVersion: () => void;
  refreshCampaigns: () => Promise<void>;
}

export function useCampaignHandlers({
  templateId,
  campaign,
  editName,
  editSubject,
  editBody,
  editSendWindowStart,
  editSendWindowEnd,
  editSendDays,
  chatInput,
  sortField,
  sortDirection,
  recipients,
  selectedRecipientIds,
  setCampaign,
  setEditName,
  setIsEditingName,
  setIsEditingEmail,
  setIsSettingsExpanded,
  setShowChangeConfirmation,
  setPendingChanges,
  setChatInput,
  setChatMessages,
  setIsChatLoading,
  setSortField,
  setSortDirection,
  setExpandedRecipients,
  setShowSendConfirmation,
  setIsActivating,
  setRecipients,
  setRecipientsTotal,
  setCurrentPage,
  setSelectedRecipientIds,
  setEmailTemplates,
  incrementRecipientsVersion,
  refreshCampaigns,
}: UseCampaignHandlersProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleSaveName = useCallback(async () => {
    if (campaign && templateId) {
      try {
        await apiClient.updateCampaign(templateId, { name: editName });
        setCampaign({ ...campaign, name: editName });
        showToast('Campaign name updated', 'success');
      } catch (error) {
        console.error('Failed to update campaign name:', error);
        showToast('Failed to update campaign name', 'error');
      }
    }
    setIsEditingName(false);
  }, [campaign, templateId, editName, setCampaign, setIsEditingName, showToast]);

  const handleCancelNameEdit = useCallback(() => {
    setEditName(campaign?.name || '');
    setIsEditingName(false);
  }, [campaign, setEditName, setIsEditingName]);

  const toggleStatus = useCallback(async () => {
    if (campaign && templateId) {
      if (campaign.status === 'active') {
        try {
          await apiClient.pauseCampaign(templateId);
          setCampaign({
            ...campaign,
            status: 'paused'
          });
          showToast('Campaign paused', 'success');
        } catch (error) {
          console.error('Failed to pause campaign:', error);
          showToast('Failed to pause campaign', 'error');
        }
      } else {
        // Block activation if no email content configured
        if (!campaign.subject?.trim() && !campaign.body?.trim()) {
          showToast('Please configure email content before activating', 'error');
          return;
        }
        setShowSendConfirmation(true);
      }
    }
  }, [campaign, templateId, setCampaign, showToast, setShowSendConfirmation]);

  const confirmActivation = useCallback(async () => {
    if (!campaign || !templateId) return;

    setIsActivating(true);
    try {
      await apiClient.activateCampaign(templateId);
      setCampaign({
        ...campaign,
        status: 'active'
      });
      showToast('Campaign activated - emails are being sent', 'success');
      setShowSendConfirmation(false);
    } catch (error: unknown) {
      console.error('Failed to activate campaign:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to activate campaign';
      if (errorMessage.includes('Email content not configured')) {
        showToast('Please configure email content before activating', 'error');
      } else if (errorMessage.includes('No email account connected')) {
        showToast('Please connect a Gmail account in Settings', 'error');
      } else if (errorMessage.includes('no enrolled recipients')) {
        showToast('Add recipients before activating', 'error');
      } else {
        showToast(errorMessage, 'error');
      }
    } finally {
      setIsActivating(false);
    }
  }, [campaign, templateId, setCampaign, showToast, setShowSendConfirmation, setIsActivating]);

  const handleSaveEmail = useCallback(() => {
    if (campaign && (editSubject !== campaign.subject || editBody !== campaign.body)) {
      setPendingChanges({
        type: 'email',
        subject: editSubject,
        body: editBody
      });
      setIsEditingEmail(false);
      setShowChangeConfirmation(true);
    } else {
      setIsEditingEmail(false);
    }
  }, [campaign, editSubject, editBody, setPendingChanges, setIsEditingEmail, setShowChangeConfirmation]);

  const handleSaveSettings = useCallback(() => {
    if (campaign) {
      const hasChanges =
        editSendWindowStart !== campaign.sendWindow.start ||
        editSendWindowEnd !== campaign.sendWindow.end ||
        JSON.stringify([...editSendDays].sort()) !== JSON.stringify([...campaign.sendWindow.days].sort());

      if (hasChanges) {
        setPendingChanges({
          type: 'settings',
          sendWindow: {
            start: editSendWindowStart,
            end: editSendWindowEnd,
            days: editSendDays
          }
        });
        setIsSettingsExpanded(false);
        setShowChangeConfirmation(true);
      } else {
        setIsSettingsExpanded(false);
      }
    }
  }, [campaign, editSendWindowStart, editSendWindowEnd, editSendDays, setPendingChanges, setIsSettingsExpanded, setShowChangeConfirmation]);

  const applyChanges = useCallback(async (pendingChanges: PendingChanges | null, restartCampaign: boolean) => {
    if (!campaign || !pendingChanges || !templateId) return;

    try {
      if (pendingChanges.type === 'email') {
        await apiClient.updateCampaign(templateId, {
          email_subject: pendingChanges.subject,
          email_body: pendingChanges.body,
        });
        setCampaign({
          ...campaign,
          subject: pendingChanges.subject || campaign.subject,
          body: pendingChanges.body || campaign.body
        });
        showToast('Email content saved', 'success');
      } else if (pendingChanges.type === 'settings' && pendingChanges.sendWindow) {
        await apiClient.updateCampaign(templateId, {
          send_window_start: pendingChanges.sendWindow.start,
          send_window_end: pendingChanges.sendWindow.end,
          send_days: pendingChanges.sendWindow.days,
        });
        setCampaign({
          ...campaign,
          sendWindow: pendingChanges.sendWindow
        });
        showToast('Settings updated', 'success');
      }

      if (restartCampaign) {
        console.log('Restarting campaign with new settings');
      } else {
        console.log('Applying changes to remaining emails');
      }

      // Only clear pending changes on success
      setPendingChanges(null);
      setShowChangeConfirmation(false);
    } catch (error) {
      console.error('Failed to apply changes:', error);
      showToast('Failed to save changes', 'error');
      // Keep modal open and pendingChanges intact so user can retry
    }
  }, [campaign, templateId, setCampaign, setPendingChanges, setShowChangeConfirmation, showToast]);

  const handleChatSend = useCallback(() => {
    if (!chatInput.trim() || !campaign) return;

    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { id: `user-${Date.now()}`, role: 'user', content: userMessage, timestamp: new Date() }]);
    setChatInput('');
    setIsChatLoading(true);

    setTimeout(() => {
      const response = generateChatResponse(userMessage, {
        name: campaign.name,
        status: campaign.status,
        stats: campaign.stats,
        recipients: campaign.recipients,
      });
      setChatMessages(prev => [...prev, { id: `ai-${Date.now()}`, role: 'assistant', content: response, timestamp: new Date() }]);
      setIsChatLoading(false);
    }, 600);
  }, [chatInput, campaign, setChatMessages, setChatInput, setIsChatLoading]);

  /**
   * Client-side sort of the current page of recipients.
   * Search and filter are handled server-side.
   */
  const getSortedRecipients = useCallback(() => {
    if (!recipients.length) return [];
    const sorted = [...recipients];

    if (sortField) {
      const statusOrder: Record<string, number> = { replied: 5, clicked: 4, opened: 3, delivered: 2, sent: 1, bounced: 0 };
      sorted.sort((a, b) => {
        let comparison = 0;
        switch (sortField) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'status': {
            comparison = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
            break;
          }
          case 'engagement': {
            const engA = getEngagementInsight(a);
            const engB = getEngagementInsight(b);
            comparison = engA.score - engB.score;
            break;
          }
          case 'activity': {
            const timeA = new Date(a.activity[a.activity.length - 1]?.timestamp ?? a.sentAt ?? '').getTime();
            const timeB = new Date(b.activity[b.activity.length - 1]?.timestamp ?? b.sentAt ?? '').getTime();
            comparison = timeA - timeB;
            break;
          }
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return sorted;
  }, [recipients, sortField, sortDirection]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  }, [sortField, sortDirection, setSortField, setSortDirection]);

  const handleToggleExpanded = useCallback((id: string) => {
    setExpandedRecipients(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [setExpandedRecipients]);

  const handleDelete = useCallback(async () => {
    if (templateId) {
      try {
        await apiClient.deleteCampaign(templateId);
        showToast('Campaign deleted', 'success');
        refreshCampaigns();
        navigate('/campaigns');
      } catch (error) {
        console.error('Failed to delete campaign:', error);
        showToast('Failed to delete campaign', 'error');
      }
    } else {
      navigate('/campaigns');
    }
  }, [templateId, navigate, showToast, refreshCampaigns]);

  const handleRemoveRecipient = useCallback(async (recipientId: string) => {
    if (!campaign || !templateId) return;
    try {
      await apiClient.removeRecipient(templateId, recipientId);
      // Optimistic local removal from recipients array
      setRecipients(prev => prev.filter(r => r.id !== recipientId));
      setRecipientsTotal(prev => Math.max(0, (typeof prev === 'number' ? prev : 0) - 1));
      // Refresh campaign stats
      const campaignData = await apiClient.getCampaign(templateId);
      const senderEmail = campaign.senderEmail;
      const data = adaptCampaignToUI(campaignData, [], senderEmail);
      setCampaign(data);
      // Trigger recipients list refresh
      incrementRecipientsVersion();
      showToast('Recipient removed', 'success');
    } catch (error) {
      console.error('Failed to remove recipient:', error);
      showToast('Failed to remove recipient', 'error');
    }
  }, [campaign, templateId, setCampaign, setRecipients, setRecipientsTotal, incrementRecipientsVersion, showToast]);

  const handleRecipientsAdded = useCallback(async () => {
    if (!templateId || !campaign) return;
    try {
      // Refresh campaign stats
      const campaignData = await apiClient.getCampaign(templateId);
      const senderEmail = campaign.senderEmail;
      const gmailStatus = await apiClient.getGmailConnectionStatus().catch(() => ({ is_connected: false, gmail_email: null }));
      if (gmailStatus.is_connected && gmailStatus.gmail_email) {
        const data = adaptCampaignToUI(campaignData, [], gmailStatus.gmail_email);
        setCampaign(data);
      } else {
        const data = adaptCampaignToUI(campaignData, [], senderEmail);
        setCampaign(data);
      }
      // Reset to page 1 to see newly added recipients
      setCurrentPage(1);
      // Trigger recipients list refresh
      incrementRecipientsVersion();
    } catch (error) {
      console.error('Failed to refresh campaign data:', error);
    }
  }, [templateId, campaign, setCampaign, setCurrentPage, incrementRecipientsVersion]);

  // Pagination handler
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedRecipientIds(new Set());
    setExpandedRecipients(new Set());
  }, [setCurrentPage, setSelectedRecipientIds, setExpandedRecipients]);

  // Selection handlers
  const handleToggleRecipientSelection = useCallback((id: string) => {
    setSelectedRecipientIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, [setSelectedRecipientIds]);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedRecipientIds(prev => {
      if (prev.size === recipients.length && recipients.length > 0) {
        return new Set();
      }
      return new Set(recipients.map(r => r.id));
    });
  }, [recipients, setSelectedRecipientIds]);

  // Batch remove handler
  const handleBatchRemove = useCallback(async () => {
    if (!templateId || !campaign || selectedRecipientIds.size === 0) return;
    try {
      const ids = Array.from(selectedRecipientIds);
      await apiClient.batchRemoveRecipients(templateId, ids);
      // Remove from local state
      setRecipients(prev => prev.filter(r => !selectedRecipientIds.has(r.id)));
      setRecipientsTotal(prev => Math.max(0, (typeof prev === 'number' ? prev : 0) - ids.length));
      setSelectedRecipientIds(new Set());
      // Refresh campaign stats
      const campaignData = await apiClient.getCampaign(templateId);
      const data = adaptCampaignToUI(campaignData, [], campaign.senderEmail);
      setCampaign(data);
      showToast(`Removed ${ids.length} recipient${ids.length > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      console.error('Failed to batch remove recipients:', error);
      showToast('Failed to remove recipients', 'error');
    }
  }, [templateId, campaign, selectedRecipientIds, setCampaign, setRecipients, setRecipientsTotal, setSelectedRecipientIds, showToast]);

  const refreshTemplates = useCallback(async () => {
    try {
      const data = await apiClient.getEmailTemplates();
      const templates: EmailTemplate[] = (data.templates || data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        subject: t.subject || '',
        body: t.body || '',
      }));
      setEmailTemplates(templates);
    } catch (error) {
      console.error('Failed to refresh email templates:', error);
    }
  }, [setEmailTemplates]);

  const handleSaveAsTemplate = useCallback(async (name: string, subject: string, body: string) => {
    try {
      await apiClient.createEmailTemplate({ name, subject, body });
      showToast('Template saved', 'success');
      await refreshTemplates();
      // Also save content to campaign
      if (campaign && templateId) {
        await apiClient.updateCampaign(templateId, {
          email_subject: subject,
          email_body: body,
        });
        setCampaign({
          ...campaign,
          subject,
          body,
        });
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      showToast('Failed to save template', 'error');
    }
  }, [campaign, templateId, setCampaign, showToast, refreshTemplates]);

  return {
    handleSaveName,
    handleCancelNameEdit,
    toggleStatus,
    confirmActivation,
    handleSaveEmail,
    handleSaveSettings,
    applyChanges,
    handleChatSend,
    getSortedRecipients,
    handleSort,
    handleToggleExpanded,
    handleDelete,
    handleRemoveRecipient,
    handleRecipientsAdded,
    handlePageChange,
    handleToggleRecipientSelection,
    handleToggleSelectAll,
    handleBatchRemove,
    handleSaveAsTemplate,
    refreshTemplates,
  };
}
